// client/src/components/common/AppLink.js
import React, { useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import TabContext from '../../context/TabContext';

/**
 * A link for shell chrome that has to work in both shells.
 *
 * Untabbed, the shell sits inside the BrowserRouter and a plain <Link> is right.
 * Tabbed, the shell sits *outside* every router — each tab owns its own — so a
 * <Link> would have no router to talk to. There we render a real anchor and send
 * the click into the active tab.
 *
 * It stays a real <a href> either way, so the browser's own middle-click and
 * ctrl-click behaviour still applies until the tab strip takes those over.
 *
 * `to` is a path string; this is shell navigation, not general-purpose routing.
 */
export default function AppLink({ to, ...rest }) {
  const tabs = useContext(TabContext);
  return tabs ? (
    <TabLink to={to} navigateActiveTab={tabs.navigateActiveTab} {...rest} />
  ) : (
    <Link to={to} {...rest} />
  );
}

function TabLink({ to, navigateActiveTab, onClick, children, ...rest }) {
  const handleClick = useCallback(
    (event) => {
      if (onClick) onClick(event);
      // Leave modified clicks to the browser — those are "open elsewhere" gestures.
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      event.preventDefault();
      navigateActiveTab(to);
    },
    [to, onClick, navigateActiveTab]
  );

  return (
    <a href={to} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
